import type { Diorama } from '../../game/three/Diorama';

/**
 * Bottom-left perf chip — FPS + frame time + Three.js renderer memory
 * stats so leaks / GPU spikes are visible while playing.
 *
 * The original tiny FPS counter has been folded in. The expanded chip
 * shows three lines:
 *   FPS / ms                                  (frame perf)
 *   Geometries / textures / draw calls        (GPU resources)
 *   JS heap MB (Chrome) + leak warnings       (runtime issues)
 *
 * The whole row turns amber when a warning threshold is crossed
 * (geometries > 6000, textures > 200, JS heap > 1500 MB, FPS < 30).
 * Color codes the FPS digit independently: green ≥55, yellow 30–55,
 * red <30.
 *
 * Throttled to a 4 Hz refresh — these counters are reasonably steady,
 * and re-painting every frame would itself be a small leak (string
 * allocs piling onto the layout thread).
 */

const GEO_WARN  = 6000;
const TEX_WARN  = 200;
const HEAP_WARN = 1500;     // MB
const FPS_WARN  = 30;

interface PerfChrome { memory?: { usedJSHeapSize?: number; totalJSHeapSize?: number; jsHeapSizeLimit?: number } }

export class FpsCounter {
  private root: HTMLDivElement;
  private rowFps: HTMLDivElement;
  private rowGpu: HTMLDivElement;
  private rowHeap: HTMLDivElement;
  private emaDt = 1 / 60;
  private nextWriteAt = 0;
  private diorama: Diorama | null = null;
  /** Highest geometry/texture count seen since boot — surfaces leaks
   *  even after a fix has stabilized the current count. */
  private peakGeometries = 0;
  private peakTextures   = 0;

  constructor(parent: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'ui-fps';
    this.root.innerHTML = `
      <div class="ui-fps__row" data-row="fps">
        <span class="ui-fps__num" data-field="fps">--</span>
        <span class="ui-fps__unit">fps</span>
        <span class="ui-fps__ms" data-field="ms">--</span>
      </div>
      <div class="ui-fps__row" data-row="gpu">
        <span class="ui-fps__lbl">geo</span><span class="ui-fps__val" data-field="geo">0</span>
        <span class="ui-fps__lbl">tex</span><span class="ui-fps__val" data-field="tex">0</span>
        <span class="ui-fps__lbl">draw</span><span class="ui-fps__val" data-field="draw">0</span>
      </div>
      <div class="ui-fps__row" data-row="heap">
        <span class="ui-fps__lbl">heap</span><span class="ui-fps__val" data-field="heap">—</span>
        <span class="ui-fps__warn" data-field="warn" hidden></span>
      </div>
    `;
    parent.appendChild(this.root);
    this.rowFps  = this.root.querySelector('[data-row="fps"]')  as HTMLDivElement;
    this.rowGpu  = this.root.querySelector('[data-row="gpu"]')  as HTMLDivElement;
    this.rowHeap = this.root.querySelector('[data-row="heap"]') as HTMLDivElement;
    void this.rowFps; void this.rowGpu; void this.rowHeap;     // keep refs for future tuning
  }

  /** Inject the diorama AFTER construction (main.ts builds the FPS
   *  counter early but the diorama later). All renderer-info fields
   *  stay at zero until this is set. */
  attachDiorama(d: Diorama) {
    this.diorama = d;
  }

  /** Show or hide the perf chip. Driven by the settings panel — when
   *  hidden the update loop still runs (the values stay current for
   *  whenever the player toggles it back), only the DOM is suppressed. */
  setVisible(v: boolean) {
    this.root.style.display = v ? '' : 'none';
  }

  update(dt: number, now: number) {
    const clamped = Math.min(dt, 0.5);
    const alpha = 0.065;
    this.emaDt = this.emaDt * (1 - alpha) + clamped * alpha;
    if (now < this.nextWriteAt) return;
    this.nextWriteAt = now + 250;
    const fps = 1 / this.emaDt;
    const ms  = this.emaDt * 1000;
    this.set('fps', fps.toFixed(0));
    this.set('ms', `${ms.toFixed(1)}ms`);
    this.root.dataset.tier = fps >= 55 ? 'good' : fps >= FPS_WARN ? 'mid' : 'bad';

    let warn = false;
    let warnText = '';
    if (this.diorama) {
      const p = this.diorama.perfInfo();
      this.peakGeometries = Math.max(this.peakGeometries, p.geometries);
      this.peakTextures   = Math.max(this.peakTextures,   p.textures);
      this.set('geo',  `${p.geometries}`);
      this.set('tex',  `${p.textures}`);
      this.set('draw', `${p.drawCalls}`);
      if (p.geometries > GEO_WARN) { warn = true; warnText = `geo>${GEO_WARN} (leak?)`; }
      else if (p.textures > TEX_WARN) { warn = true; warnText = `tex>${TEX_WARN}`; }
    }
    // performance.memory is Chromium-only and reports the JS heap, not
    // the whole tab. Tab RAM (what taskmgr shows) is JS heap + WebGL
    // buffers + audio + DOM — usually 2-3× the JS heap on a busy tab.
    const perfMem = (performance as Performance & PerfChrome).memory;
    if (perfMem && typeof perfMem.usedJSHeapSize === 'number') {
      const heapMB = perfMem.usedJSHeapSize / (1024 * 1024);
      this.set('heap', `${heapMB.toFixed(0)}MB`);
      if (heapMB > HEAP_WARN) { warn = true; warnText = warnText || `heap>${HEAP_WARN}MB`; }
    } else {
      this.set('heap', '—');
    }
    if (fps < FPS_WARN) { warn = true; warnText = warnText || `fps<${FPS_WARN}`; }

    const warnEl = this.root.querySelector('[data-field="warn"]') as HTMLElement | null;
    if (warnEl) {
      warnEl.hidden = !warn;
      warnEl.textContent = warn ? `⚠ ${warnText}` : '';
    }
    this.root.classList.toggle('warn', warn);
  }

  private set(field: string, text: string) {
    const el = this.root.querySelector(`[data-field="${field}"]`);
    if (el && el.textContent !== text) el.textContent = text;
  }
}
